import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Globe, Sparkles, Package, Check, Loader2, Target, ChevronDown, ChevronUp, Plus, AlertCircle, RefreshCw, Clock, PenLine, FileUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEIGHT_LABELS = { 1: 'Nice to Know', 2: 'Helpful', 3: 'Important', 4: 'Critical', 5: 'Deal Breaker' };
const TIER_COLORS = { starter: 'bg-zinc-100 text-zinc-700', mid: 'bg-brand-accent-muted text-brand-primary', enterprise: 'bg-violet-100 text-violet-700', unknown: 'bg-zinc-100 text-zinc-600' };

export default function ProductIntelligence({ frameworks = [] }) {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [targetFramework, setTargetFramework] = useState('');
  const [applying, setApplying] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [catalogueFile, setCatalogueFile] = useState(null);
  const [catalogueScanning, setCatalogueScanning] = useState(false);
  const catalogueInputRef = useRef(null);
  const [manualProduct, setManualProduct] = useState({ name: '', description: '', features: '', ideal_customer: '', price_tier: 'mid' });

  useEffect(() => {
    loadLastScan();
  }, []);

  const loadLastScan = async () => {
    let prefilled = '';
    try {
      const { data } = await axios.get(`${API}/product-intelligence/last-scan`);
      if (data.scan_result) {
        setLastScan(data);
        setScanResult(data.scan_result);
        prefilled = data.website_url || '';
        setWebsiteUrl(prefilled);
      }
    } catch (e) {
      // No previous scan
    }
    if (!prefilled) {
      try {
        const { data: cs } = await axios.get(`${API}/company/settings`);
        if (cs?.company_website) setWebsiteUrl(cs.company_website);
      } catch (e) { /* non-blocking */ }
    }
  };

  const handleScan = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }
    setScanning(true);
    setScanResult(null);
    try {
      const { data } = await axios.post(`${API}/product-intelligence/scan-website`, {
        website_url: websiteUrl,
        additional_context: additionalContext || undefined,
      });
      setScanResult(data.scan_result);
      setLastScan(data);
      toast.success('Website scanned! Review the AI recommendations below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleCatalogueScan = async () => {
    if (!catalogueFile) {
      toast.error('Choose a catalogue file first');
      return;
    }
    setCatalogueScanning(true);
    setScanResult(null);
    try {
      const form = new FormData();
      form.append('file', catalogueFile);
      if (additionalContext) form.append('additional_context', additionalContext);
      const { data } = await axios.post(`${API}/product-intelligence/scan-catalogue`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setScanResult(data.scan_result);
      setLastScan(data);
      setCatalogueFile(null);
      if (catalogueInputRef.current) catalogueInputRef.current.value = '';
      toast.success('Catalogue analyzed! Review the AI recommendations below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Catalogue scan failed. Please try again.');
    } finally {
      setCatalogueScanning(false);
    }
  };

  const toggleProduct = (index) => {
    setSelectedProducts(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const toggleQuestion = (index) => {
    setSelectedQuestions(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const selectAll = () => {
    if (scanResult) {
      setSelectedProducts(scanResult.products.map((_, i) => i));
      setSelectedQuestions(scanResult.general_qualification_questions.map((_, i) => i));
    }
  };

  const handleApply = async () => {
    if (selectedProducts.length === 0 && selectedQuestions.length === 0) {
      toast.error('Select at least one product or question to add');
      return;
    }
    if (selectedQuestions.length > 0 && !targetFramework) {
      toast.error('Select a framework to add questions to');
      return;
    }
    setApplying(true);
    try {
      const products = selectedProducts.map(i => scanResult.products[i]);
      const questions = selectedQuestions.map(i => scanResult.general_qualification_questions[i]);

      const { data } = await axios.post(`${API}/product-intelligence/apply-recommendations`, {
        products,
        questions,
        framework_id: targetFramework || undefined,
      });

      toast.success(data.message);
      setSelectedProducts([]);
      setSelectedQuestions([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply recommendations');
    } finally {
      setApplying(false);
    }
  };

  const toggleExpand = (idx) => {
    setExpandedProducts(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleManualAdd = async () => {
    if (!manualProduct.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    setApplying(true);
    try {
      const features = manualProduct.features ? manualProduct.features.split(',').map(f => f.trim()).filter(Boolean) : [];
      await axios.post(`${API}/product-intelligence/apply-recommendations`, {
        products: [{
          name: manualProduct.name,
          description: manualProduct.description,
          features,
          ideal_customer: manualProduct.ideal_customer,
          price_tier: manualProduct.price_tier,
          discovery_questions: [],
        }],
        questions: [],
      });
      toast.success(`"${manualProduct.name}" added to your product catalog`);
      setManualProduct({ name: '', description: '', features: '', ideal_customer: '', price_tier: 'mid' });
      setShowManualAdd(false);
    } catch (e) {
      toast.error('Failed to add product');
    } finally {
      setApplying(false);
    }
  };

  const daysSinceLastScan = lastScan?.scanned_at
    ? Math.floor((Date.now() - new Date(lastScan.scanned_at).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="space-y-6" data-testid="product-intelligence">
      {/* Scanner */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="w-4 h-4 text-brand-primary" />
                AI Product Scanner
              </CardTitle>
              <p className="text-sm text-zinc-500 mt-1">
                Enter your company website and PipeGuide will analyze your products to generate qualification questions and solution mapping.
              </p>
            </div>
            {lastScan?.scanned_at && (
              <div className="text-right flex-shrink-0 ml-4">
                <div className="flex items-center gap-1 text-xs text-zinc-400">
                  <Clock className="w-3 h-3" />
                  Last scanned {daysSinceLastScan === 0 ? 'today' : `${daysSinceLastScan} day${daysSinceLastScan === 1 ? '' : 's'} ago`}
                </div>
                {daysSinceLastScan >= 30 && (
                  <span className="text-xs text-amber-600 font-medium">Consider re-scanning for updates</span>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="https://yourcompany.com"
              className="flex-1 rounded-md"
              data-testid="website-url-input"
            />
            <Button
              onClick={handleScan}
              disabled={scanning || !websiteUrl.trim()}
              className="bg-brand-primary hover:bg-brand-hover rounded-md"
              data-testid="scan-website-btn"
            >
              {scanning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
              ) : lastScan?.scan_result ? (
                <><RefreshCw className="w-4 h-4 mr-2" /> Re-scan</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Scan & Analyze</>
              )}
            </Button>
          </div>
          <Input
            value={additionalContext}
            onChange={e => setAdditionalContext(e.target.value)}
            placeholder="Optional: Add context about your products (e.g., 'We sell to mid-market manufacturers' or 'Focus on our healthcare vertical')"
            className="rounded-md text-sm"
          />

          {/* Catalogue upload — alternative source for product info */}
          <div className="border-t border-zinc-100 pt-4">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <FileUp className="w-3.5 h-3.5" />
              Or upload a product catalogue
            </p>
            <div className="flex gap-3 items-center">
              <input
                ref={catalogueInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.csv,.md"
                onChange={e => setCatalogueFile(e.target.files?.[0] || null)}
                className="flex-1 text-sm text-zinc-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-zinc-100 file:text-zinc-700 file:text-sm file:cursor-pointer hover:file:bg-zinc-200 cursor-pointer"
                data-testid="catalogue-file-input"
              />
              <Button
                onClick={handleCatalogueScan}
                disabled={catalogueScanning || scanning || !catalogueFile}
                variant="outline"
                className="rounded-md"
                data-testid="scan-catalogue-btn"
              >
                {catalogueScanning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Upload & Analyze</>
                )}
              </Button>
            </div>
            <p className="text-xs text-zinc-400 mt-1.5">PDF, DOCX, TXT or CSV — max 10MB. Great for price lists, brochures and spec sheets the website doesn't cover.</p>
          </div>

          {(scanning || catalogueScanning) && (
            <div className="bg-brand-muted border border-brand-accent rounded-lg p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
              <div>
                <p className="text-sm font-medium text-brand-primary">{catalogueScanning ? 'Analyzing your catalogue...' : 'Analyzing your website...'}</p>
                <p className="text-xs text-brand-primary">This typically takes 15-30 seconds. We're extracting your products, pricing, and features.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Product Add */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <PenLine className="w-4 h-4 text-violet-500" />
              Manual Product Add
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowManualAdd(!showManualAdd)}
              className="rounded-md"
              data-testid="toggle-manual-add"
            >
              {showManualAdd ? 'Cancel' : <><Plus className="w-4 h-4 mr-1" /> Add Product Manually</>}
            </Button>
          </div>
          <p className="text-sm text-zinc-500">
            Add products manually if the AI scanner missed something or you're working on new solutions not yet on your website.
          </p>
        </CardHeader>
        {showManualAdd && (
          <CardContent className="space-y-3 pt-0">
            <Input
              value={manualProduct.name}
              onChange={e => setManualProduct(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Product name"
              className="rounded-md"
              data-testid="manual-product-name"
            />
            <Textarea
              value={manualProduct.description}
              onChange={e => setManualProduct(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What does this product do and who is it for?"
              className="rounded-md resize-none h-20"
            />
            <Input
              value={manualProduct.features}
              onChange={e => setManualProduct(prev => ({ ...prev, features: e.target.value }))}
              placeholder="Key features (comma-separated, e.g. 'on-site installation, 24/7 support, custom reporting')"
              className="rounded-md"
            />
            <Input
              value={manualProduct.ideal_customer}
              onChange={e => setManualProduct(prev => ({ ...prev, ideal_customer: e.target.value }))}
              placeholder="Ideal customer profile (e.g. 'Mid-market companies with 50–500 employees in your industry')"
              className="rounded-md"
            />
            <div className="flex items-center gap-3">
              <Select value={manualProduct.price_tier} onValueChange={v => setManualProduct(prev => ({ ...prev, price_tier: v }))}>
                <SelectTrigger className="w-40 rounded-md"><SelectValue placeholder="Price tier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="mid">Mid-Market</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={handleManualAdd}
                disabled={applying || !manualProduct.name.trim()}
                className="bg-violet-600 hover:bg-violet-700 rounded-md"
                data-testid="save-manual-product"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add to Catalog</>}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Results */}
      {scanResult && (
        <>
          {/* Company Summary */}
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-900">AI Analysis Complete</p>
                  <p className="text-sm text-emerald-800 mt-1">{scanResult.company_summary}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Bar */}
          <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg p-3">
            <div className="text-sm text-zinc-600">
              <span className="font-medium">{selectedProducts.length}</span> products and <span className="font-medium">{selectedQuestions.length}</span> questions selected
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll} className="rounded-md" data-testid="select-all-btn">
                Select All
              </Button>
              {selectedQuestions.length > 0 && (
                <Select value={targetFramework} onValueChange={setTargetFramework}>
                  <SelectTrigger className="w-48 rounded-md h-8 text-sm">
                    <SelectValue placeholder="Add questions to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {frameworks.map(fw => (
                      <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                onClick={handleApply}
                disabled={applying || (selectedProducts.length === 0 && selectedQuestions.length === 0)}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 rounded-md"
                data-testid="apply-recommendations-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Apply Selected</>}
              </Button>
            </div>
          </div>

          {/* Products */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              <Package className="w-4 h-4" /> Recommended Products ({scanResult.products?.length || 0})
            </h3>
            {scanResult.products?.map((product, idx) => (
              <Card
                key={idx}
                className={`border transition-all cursor-pointer ${
                  selectedProducts.includes(idx) ? 'border-emerald-400 bg-emerald-50/30' : 'border-zinc-200 hover:border-zinc-300'
                }`}
                onClick={() => toggleProduct(idx)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${
                          selectedProducts.includes(idx) ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-300'
                        }`}>
                          {selectedProducts.includes(idx) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="font-semibold text-zinc-900">{product.name}</span>
                        <Badge className={`text-xs ${TIER_COLORS[product.price_tier] || TIER_COLORS.unknown}`}>
                          {product.price_tier}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-600 ml-7">{product.description}</p>
                      {product.features?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2 ml-7">
                          {product.features.slice(0, 4).map((f, i) => (
                            <span key={i} className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{f}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleExpand(idx); }}
                      className="text-zinc-400 hover:text-zinc-600 p-1"
                    >
                      {expandedProducts[idx] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                  {expandedProducts[idx] && (
                    <div className="mt-3 ml-7 pt-3 border-t border-zinc-100 space-y-3">
                      {product.ideal_customer && (
                        <div>
                          <span className="text-xs font-medium text-zinc-500 uppercase">Ideal Customer</span>
                          <p className="text-sm text-zinc-700 mt-0.5">{product.ideal_customer}</p>
                        </div>
                      )}
                      {product.discovery_questions?.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-zinc-500 uppercase">Discovery Questions</span>
                          <div className="space-y-2 mt-1">
                            {product.discovery_questions.map((q, qi) => (
                              <div key={qi} className="bg-zinc-50 rounded-md p-2 text-sm">
                                <p className="font-medium text-zinc-800">{q.question}</p>
                                <div className="flex gap-4 mt-1 text-xs">
                                  <span className="text-emerald-600">+ {q.positive_signal}</span>
                                  <span className="text-red-500">- {q.negative_signal}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* General Questions */}
          {scanResult.general_qualification_questions?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                <Target className="w-4 h-4" /> Recommended Questions ({scanResult.general_qualification_questions.length})
              </h3>
              {scanResult.general_qualification_questions.map((q, idx) => (
                <div
                  key={idx}
                  onClick={() => toggleQuestion(idx)}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedQuestions.includes(idx) ? 'border-blue-400 bg-brand-muted/30' : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center mt-0.5 ${
                    selectedQuestions.includes(idx) ? 'bg-brand-muted0 border-brand-primary' : 'border-zinc-300'
                  }`}>
                    {selectedQuestions.includes(idx) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-zinc-900">{q.question}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{q.category}</Badge>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        q.weight >= 4 ? 'bg-orange-100 text-orange-700' : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        {q.weight}/5 — {WEIGHT_LABELS[q.weight] || 'Important'}
                      </span>
                      {q.maps_to_products?.length > 0 && (
                        <span className="text-xs text-zinc-400">Maps to: {q.maps_to_products.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Competitor Signals */}
          {scanResult.competitor_signals?.length > 0 && (
            <Card className="border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Competitor Differentiators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {scanResult.competitor_signals.map((c, i) => (
                    <div key={i} className="flex gap-2 text-sm">
                      <span className="font-medium text-zinc-700 min-w-[120px]">vs {c.competitor}:</span>
                      <span className="text-zinc-600">{c.differentiator}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
